/**
 * Lightweight click-path recorder.
 *
 * Every CTA on the site calls `recordPathStep(...)` so that when a visitor
 * finally submits a form we can report the exact trail they followed
 * ("Hero: Get your free quote > Life event: I just got married > Contact form").
 *
 * Stored in sessionStorage so it survives client-side navigation between the
 * landing page and the questionnaires but resets for a fresh visit.
 */

const KEY = "scudo-pathway";
const MAX_STEPS = 25;

type Step = { label: string; at: string };

function read(): Step[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Step[]) : [];
  } catch {
    return [];
  }
}

function write(steps: Step[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(steps.slice(-MAX_STEPS)));
  } catch {
    /* storage unavailable — pathway tracking is best-effort */
  }
}

/** Append a human-readable step to the visitor's click path. */
export function recordPathStep(label: string) {
  if (typeof window === "undefined") return;
  const steps = read();
  const last = steps[steps.length - 1];
  if (last?.label === label) return; // collapse duplicate rapid clicks
  steps.push({ label, at: new Date().toISOString() });
  write(steps);
}

/** Human-readable trail, ready to store alongside a submission. */
export function getPathway(): string {
  const steps = read();
  if (steps.length === 0) return "Direct (no tracked clicks)";
  return steps.map((s) => s.label).join(" > ");
}

export function clearPathway() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
